import React, { useState, useEffect } from 'react';
import { Card, Tabs, Tag, Button, Dropdown, Select, Spin, Image, Popconfirm, message, Tooltip } from "antd";
import EditableRow from "./EditableRow";
import { PlusOutlined, CheckOutlined, SyncOutlined, RightOutlined, LoadingOutlined } from '@ant-design/icons';
import ContactList from './ContactList';
import Channels from './Channels';
import { CustomerDetailData, CustomerPhoneEntry } from "../../../api/ChannelCustomer";
import CustomerPhones from "../../../components/Customer/CustomerPhones";
import { useTranslation } from 'react-i18next';
import CustomerImgList from './CustomerImgList';
import { getCustomerPhotos, uploadCustomerPhoto, deleteCustomerPhoto, CustomerPhoto } from "../../../api/CustomerPhoto";
import CallReportFail from './CallReportFail';
import { isEditableFallbackTagName } from "../../../lib/customerTag";
import { getNextTags } from "../../../utils/appointmentNextTags";

// Tag tự cắt chữ kèm "…" khi quá dài, hover hiện đầy đủ
const EllipsisTag = ({ color, children }: { color?: string; children: React.ReactNode }) => (
    <Tooltip title={children}>
        <Tag color={color || "default"} className="mr-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap align-middle">
            {children}
        </Tag>
    </Tooltip>
);

interface Props {
    customer: CustomerDetailData | null;
    customerTags: { label: string; value: number; color?: string }[];
    careStatuses: { label: string; value: number; color?: string }[];
    occupations: { label: string; value: number }[];
    onUpdateField: (field: string, value: any) => Promise<void>;
    onUpdateCareStatuses: (ids: number[]) => Promise<void>;
    onUpdateAddress: (data: { province_code: string; ward_code: string; address: string }) => Promise<void>;
    onUpdatePhones: (phones: CustomerPhoneEntry[]) => Promise<void>;
}

export default function LeftInformationPanel({ customer, customerTags, careStatuses, occupations, onUpdateField, onUpdateCareStatuses, onUpdateAddress, onUpdatePhones }: Props) {
    const { t } = useTranslation();
    const [selectedTagValues, setSelectedTagValues] = useState<number[]>([]);
    const [updatingField, setUpdatingField] = useState<string | null>(null);

    // Hình ảnh của khách hàng (GET /be/customers/{id}/photos) hiển thị dưới phần Ghi chú
    const [customerPhotos, setCustomerPhotos] = useState<CustomerPhoto[]>([]);
    useEffect(() => {
        const cid = customer?.id;
        if (!cid) { setCustomerPhotos([]); return; }
        let cancelled = false;
        getCustomerPhotos(cid, { limit: 100 }).then((res) => {
            if (!cancelled && res.status) setCustomerPhotos(res.items);
        });
        return () => { cancelled = true; };
    }, [customer?.id]);

    // Tải lên ảnh mới (POST /be/customers/{id}/photos — photos[], ≤10MB/ảnh) và xóa ảnh (DELETE /be/customers/photos/{photo_id})
    const photoInputRef = React.useRef<HTMLInputElement>(null);
    const [uploadingPhotos, setUploadingPhotos] = useState(false);
    const handlePhotoInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        e.target.value = ""; // cho phép chọn lại cùng file lần sau
        const cid = customer?.id;
        if (!cid || files.length === 0) return;
        // Kiểm tra định dạng + dung lượng theo giới hạn API
        const valid = files.filter((f) => {
            if (!f.type.startsWith("image/")) {
                message.error(t("customer.photo_invalid_type") || "Chỉ chấp nhận tệp hình ảnh");
                return false;
            }
            if (f.size > 10 * 1024 * 1024) {
                message.error(t("customer.photo_too_large", { name: f.name }) || `Ảnh "${f.name}" vượt quá 10MB`);
                return false;
            }
            return true;
        });
        if (valid.length === 0) return;
        setUploadingPhotos(true);
        const results = await Promise.all(valid.map((f) => uploadCustomerPhoto(cid, f)));
        const okCount = results.filter((r) => r.status).length;
        if (okCount > 0) {
            message.success(t("cd.photo_upload_success", { count: okCount }) || `Đã tải lên ${okCount} ảnh`);
            // Nạp lại danh sách để có URL/tiêu đề chuẩn từ server
            const res = await getCustomerPhotos(cid, { limit: 100 });
            if (res.status) setCustomerPhotos(res.items);
        }
        if (okCount < valid.length) {
            const firstErr = results.find((r) => !r.status)?.msg;
            message.error(firstErr || t("cd.photo_upload_failed") || "Tải ảnh lên thất bại");
        }
        setUploadingPhotos(false);
    };

    const handleDeletePhoto = async (photoId: number) => {
        const res = await deleteCustomerPhoto(photoId);
        if (res.status) {
            setCustomerPhotos((prev) => prev.filter((p) => p.id !== photoId));
            message.success(res.msg || t("cd.photo_delete_success") || "Đã xóa hình ảnh");
        } else {
            message.error(res.msg || t("cd.photo_delete_failed") || "Xóa hình ảnh thất bại");
        }
    };

    // ID khách hàng theo kênh (lấy connection đầu tiên có channel_customers_id) để load ảnh khách gửi
    const channelCustomerId =
        (customer?.connections as any[] | undefined)?.find((c) => c?.channel_customers_id)?.channel_customers_id ?? null;

    // Sync selected tags from customer data
    useEffect(() => {
        if (customer?.customer_tag_id) {
            setSelectedTagValues(prev => prev.length === 0 ? [customer.customer_tag_id!] : prev);
        }
    }, [customer?.customer_tag_id]);

    // Ngữ cảnh "Khách hàng" (screen_context = 4) của ma trận chuyển trạng thái: backend
    // trả sẵn `dynamic_next_tags` theo phòng ban người đăng nhập + thẻ hiện tại.
    // API chưa trả trường này thì giữ luồng cũ (chọn trong nhóm group_1).
    const useDynamicTags = Array.isArray(customer?.dynamic_next_tags);
    const tagOptions = (useDynamicTags
        ? getNextTags(customer).map(t => ({ value: t.id, label: t.name, color: t.color || undefined }))
        : customerTags
    ).map(t => ({
        value: t.value,
        label: t.label,
        color: t.color || "#1890ff",
    }));

    const statusOptions = careStatuses.map(s => ({
        value: s.value,
        label: s.label,
        color: s.color || "#1890ff",
    }));

    const hasTags = selectedTagValues.length > 0;
    const getTagByValue = (val: number) => tagOptions.find(t => t.value === val);
    const tagInList = hasTags ? !!getTagByValue(selectedTagValues[0]) : false;
    const fallbackTag = !tagInList && customer?.customer_tag ? customer.customer_tag : null;
    // Thẻ nằm ngoài danh sách chỉ hiển thị tĩnh; trừ các thẻ dưới đây được phép đổi sang thẻ khác:
    //  - "Mới - Tiếp cận"
    //  - "KNC - Sai thông tin, liên hệ lần đầu báo không nhu cầu luôn"
    const fallbackTagEditable = !!fallbackTag && isEditableFallbackTagName((fallbackTag as any).name);

    // Thẻ đang giữ + có được đổi không. Theo ma trận: thẻ hiện tại lấy từ quan hệ
    // customer_tag, chỉ đổi được khi ma trận cho phép ít nhất một thẻ tiếp theo.
    const currentTag: { name?: string; color?: string | null } | null = useDynamicTags
        ? (customer?.customer_tag ?? null)
        : fallbackTag
            ? fallbackTag
            : hasTags
                ? { name: getTagByValue(selectedTagValues[0])?.label, color: getTagByValue(selectedTagValues[0])?.color }
                : null;
    const canChangeTag = useDynamicTags
        ? tagOptions.length > 0
        : !(fallbackTag && !fallbackTagEditable);

    const handleSelectTag = async (val: number | null) => {
        setSelectedTagValues(val !== null ? [val] : []);
        setUpdatingField('customer_tag_id');
        try { await onUpdateField("customer_tag_id", val); } finally { setUpdatingField(null); }
    };

    // Nguồn sự thật là quan hệ nhiều-nhiều care_statuses; customer_care_status_id
    // chỉ là trạng thái chính, dùng làm dự phòng cho bản ghi cũ chưa có pivot.
    const careStatusRelation = (customer as any)?.care_statuses as { id: number; name: string; color?: string | null }[] | undefined;
    const selectedStatusIds: number[] = careStatusRelation?.length
        ? careStatusRelation.map(s => s.id)
        : ((customer as any)?.customer_care_status_id ? [(customer as any).customer_care_status_id] : []);

    // Thẻ để hiển thị: ưu tiên nhãn trong danh sách, thiếu thì lấy tên từ quan hệ
    const selectedStatusTags = selectedStatusIds.map(id => {
        const opt = statusOptions.find(s => s.value === id);
        if (opt) return { value: id, label: opt.label, color: opt.color };
        const rel = careStatusRelation?.find(s => s.id === id);
        const fallbackName = rel?.name || (customer as any)?.customer_care_status?.name;
        return { value: id, label: fallbackName || String(id), color: rel?.color || undefined };
    });

    const isUpdatingStatus = updatingField === 'customer_care_status_ids';

    const handleToggleStatus = async (val: number) => {
        // Click vào thẻ đang chọn thì bỏ, chưa chọn thì thêm — không thay thế thẻ cũ
        const next = selectedStatusIds.includes(val)
            ? selectedStatusIds.filter(id => id !== val)
            : [...selectedStatusIds, val];
        setUpdatingField('customer_care_status_ids');
        try { await onUpdateCareStatuses(next); } finally { setUpdatingField(null); }
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return "—";
        try {
            const d = new Date(dateStr);
            const now = new Date();
            const diffMs = now.getTime() - d.getTime();
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            if (diffDays === 0) return t('cd.today');
            return t('cd.days_ago', { count: diffDays });
        } catch { return dateStr; }
    };
    return (
        <Card className="dark:bg-[#141414] dark:border-[#303030]">
            <Tabs
                className="dark:[&_.ant-tabs-tab]:text-gray-400 
                    dark:[&_.ant-tabs-tab-active]:text-yellow-400
                    dark:[&_.ant-tabs-ink-bar]:bg-yellow-400"
                defaultActiveKey="1"
                items={[
                    {
                        key: "1",
                        label: t('cd.tab_info'),
                        children: (
                            <>
                                {/* Yellow mini block */}
                                <div
                                    className="mb-4 p-4 rounded-md flex flex-col gap-2 
                                        bg-[#f6f1de] 
                                        dark:bg-[#1f1f1f] 
                                        text-000000e0"
                                >
                                    <div>{t('cd.last_contact')}
                                        <strong className="ml-1">{formatDate((customer as any)?.last_seen_at)}</strong>
                                    </div>
                                    <div>{t('cd.channel')}
                                        {customer?.connection ? (
                                            <Tag color="#1890ff" className="ml-1">
                                                {customer.connection.display_name}
                                            </Tag>
                                        ) : "—"}
                                    </div>

                                    <div className='flex gap-2 items-center min-w-0'>
                                        <span className="text-gray-700 text-000000e0 whitespace-nowrap">{t('cd.care_status')}</span>

                                        {/* Không được đổi (ma trận không cho, hoặc thẻ ngoài danh sách
                                            trừ "Mới - Tiếp cận"...): chỉ hiển thị tĩnh */}
                                        {currentTag && !canChangeTag ? (
                                            <EllipsisTag color={currentTag.color || "default"}>
                                                {currentTag.name || "—"}
                                            </EllipsisTag>
                                        ) : currentTag ? (
                                            <div className="flex items-center gap-2 min-w-0">
                                                <EllipsisTag color={currentTag.color || "default"}>
                                                    {currentTag.name || "—"}
                                                </EllipsisTag>

                                                <Dropdown
                                                    trigger={["click"]}
                                                    disabled={updatingField === 'customer_tag_id'}
                                                    menu={{
                                                        items: tagOptions.map((opt) => ({
                                                            key: opt.value,
                                                            label: (
                                                                <div className="flex items-center justify-between min-w-[140px]">
                                                                    <Tag color={opt.color} className="mr-0">{opt.label}</Tag>
                                                                    {selectedTagValues[0] === opt.value && (
                                                                        <CheckOutlined className="text-green-500 ml-2" />
                                                                    )}
                                                                </div>
                                                            ),
                                                            onClick: () => handleSelectTag(opt.value),
                                                        })),
                                                    }}
                                                >
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={updatingField === 'customer_tag_id' ? <LoadingOutlined /> : <SyncOutlined className="text-gray-400 hover:text-blue-500" />}
                                                    />
                                                </Dropdown>
                                            </div>
                                        ) : !canChangeTag ? (
                                            <span className="text-gray-400">—</span>
                                        ) : (
                                            <Select
                                                size="small"
                                                placeholder={t('cd.select_tag')}
                                                loading={updatingField === 'customer_tag_id'}
                                                className="w-[160px] dark:[&_.ant-select-selector]:bg-[#1f1f1f] 
                                                    dark:[&_.ant-select-selector]:border-[#303030]
                                                    dark:[&_.ant-select-selector]:text-gray-200"
                                                options={tagOptions.map((opt) => ({
                                                    value: opt.value,
                                                    label: <Tag color={opt.color} className="mr-0">{opt.label}</Tag>,
                                                }))}
                                                onChange={(val) => handleSelectTag(val)}
                                                value={null}
                                            />
                                        )}
                                    </div>

                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className="text-gray-700 text-000000e0 whitespace-nowrap">{t('cd.chat_tag')}</span>

                                        {selectedStatusTags.length > 0 ? (
                                            <div className="flex items-center gap-1 min-w-0 flex-wrap">
                                                {selectedStatusTags.map(tag => (
                                                    <EllipsisTag key={tag.value} color={tag.color || "default"}>
                                                        {tag.label}
                                                    </EllipsisTag>
                                                ))}

                                                <Dropdown
                                                    trigger={["click"]}
                                                    disabled={isUpdatingStatus}
                                                    overlayStyle={{ zIndex: 29 }}
                                                    dropdownRender={(menu) => (
                                                        <div
                                                            style={{
                                                                maxHeight: 300,
                                                                overflowY: 'auto',
                                                                backgroundColor: '#fff',
                                                                boxShadow: '0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08)',
                                                                borderRadius: 8
                                                            }}
                                                            // Thêm class này để chặn scroll dư từ menu con của Ant Design
                                                            className="[&_.ant-dropdown-menu]:max-h-none [&_.ant-dropdown-menu]:overflow-visible [&_.ant-dropdown-menu]:bg-transparent [&_.ant-dropdown-menu]:shadow-none"
                                                        >
                                                            {menu}
                                                        </div>
                                                    )}
                                                    menu={{
                                                        selectable: false,
                                                        onClick: ({ key, domEvent }) => {
                                                            domEvent.preventDefault();
                                                            domEvent.stopPropagation();
                                                            handleToggleStatus(Number(key));
                                                        },
                                                        items: statusOptions.map((opt) => ({
                                                            key: opt.value,
                                                            label: (
                                                                <div className="flex items-center justify-between min-w-[140px]">
                                                                    <Tag color={opt.color} className="mr-0">{opt.label}</Tag>
                                                                    {selectedStatusIds.includes(opt.value) && (
                                                                        <CheckOutlined className="text-green-500 ml-2" />
                                                                    )}
                                                                </div>
                                                            ),
                                                        })),
                                                    }}
                                                >
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={isUpdatingStatus ? <LoadingOutlined /> : <SyncOutlined className="text-gray-400 hover:text-blue-500" />}
                                                    />
                                                </Dropdown>
                                            </div>
                                        ) : (
                                            <Select
                                                size="small"
                                                placeholder={t('cd.select_status')}
                                                loading={isUpdatingStatus}
                                                className="w-[160px] dark:[&_.ant-select-selector]:bg-[#1f1f1f]
                                                    dark:[&_.ant-select-selector]:border-[#303030]
                                                    dark:[&_.ant-select-selector]:text-gray-200"
                                                options={statusOptions.map(opt => ({
                                                    value: opt.value,
                                                    label: <Tag color={opt.color} className="mr-0">{opt.label}</Tag>
                                                }))}
                                                onChange={handleToggleStatus}
                                                value={null}
                                            />
                                        )}
                                    </div>

                                </div>

                                {/* Editable rows */}
                                <EditableRow
                                    label={t('cd.fullname_real') || "Họ tên thật"}
                                    value={customer?.fullname || ""}
                                    onSave={(val) => onUpdateField("fullname", val)}
                                />
                                {/* Nguồn gốc khách (is_foreign): 0 = trong nước, 1 = nước ngoài.
                                    Khách nước ngoài thì Tỉnh/Thành - Phường/Xã không bắt buộc khi tạo lịch hẹn. */}
                                <EditableRow
                                    label={t('cd.customer_origin') || "Nguồn gốc khách"}
                                    fieldType="select"
                                    value={customer?.is_foreign != null ? String(customer.is_foreign) : "0"}
                                    options={[
                                        { label: t('cd.origin_domestic') || "Trong nước", value: 0 },
                                        { label: t('cd.origin_foreign') || "Nước ngoài", value: 1 },
                                    ]}
                                    onSave={(val) => onUpdateField("is_foreign", Number(val))}
                                />
                                <EditableRow
                                    label={t('cd.address')}
                                    fieldType="address"
                                    value={customer?.address || ""}
                                    provinceCode={customer?.province_code || ""}
                                    wardCode={customer?.ward_code || ""}
                                    onSaveAddress={onUpdateAddress}
                                />
                                <EditableRow
                                    label={t('cd.birthday')}
                                    fieldType="date"
                                    value={(customer as any)?.birthday || ""}
                                    onSave={(val) => onUpdateField("birthday", val)}
                                />
                                <EditableRow
                                    label={t('cd.gender')}
                                    fieldType="gender"
                                    value={(customer as any)?.gender || ""}
                                    onSave={(val) => onUpdateField("gender", val)}
                                />
                                <EditableRow
                                    label={t('cd.email')}
                                    value={(customer as any)?.email || ""}
                                    onSave={(val) => onUpdateField("email", val)}
                                />
                                <EditableRow
                                    label={t('cd.phone')}
                                    value={customer?.phone || ""}
                                    onSave={(val) => onUpdateField("phone", val)}
                                />
                                {/* Số điện thoại phụ: gửi cả mảng phones khi thêm/sửa/xóa */}
                                <CustomerPhones
                                    phones={(customer as any)?.phones || []}
                                    mainPhone={customer?.phone}
                                    onSave={onUpdatePhones}
                                />
                                <EditableRow
                                    label={t('cd.occupation')}
                                    fieldType="select"
                                    value={customer?.customer_occupation_id ? String(customer.customer_occupation_id) : ""}
                                    options={occupations}
                                    onSave={(val) => onUpdateField("customer_occupation_id", Number(val))}
                                />
                                <EditableRow
                                    label={t('cd.notes')}
                                    fieldType="textarea"
                                    value={customer?.note || ""}
                                    onSave={(val) => onUpdateField("note", val)}
                                />

                                {/* Hình ảnh của khách hàng — bấm để phóng to, hover để xóa, ô cuối để tải thêm ảnh */}
                                <div className="py-3 border-b border-gray-100 dark:border-[#303030]">
                                    <div className="text-gray-500 dark:text-gray-400 text-sm mb-2">
                                        {t('cd.customer_photos')} ({customerPhotos.length})
                                    </div>
                                    <Image.PreviewGroup>
                                        <div className="flex flex-wrap gap-2">
                                            {customerPhotos.map((p) => (
                                                <div key={p.id} className="relative group">
                                                    <Image
                                                        src={p.image}
                                                        width={64}
                                                        height={64}
                                                        className="object-cover rounded-md border border-gray-200 dark:border-[#303030]"
                                                    />
                                                    <Popconfirm
                                                        title={t('cd.photo_delete_confirm') || 'Xóa hình ảnh này?'}
                                                        okText={t('notif.confirm_ok') || 'Đồng ý'}
                                                        cancelText={t('notif.confirm_cancel') || 'Hủy'}
                                                        okButtonProps={{ danger: true }}
                                                        onConfirm={() => handleDeletePhoto(p.id)}
                                                    >
                                                        <button
                                                            title={t('cd.photo_delete') || 'Xóa ảnh'}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-[11px] leading-none hidden group-hover:flex items-center justify-center shadow hover:bg-red-600 z-10"
                                                        >
                                                            ✕
                                                        </button>
                                                    </Popconfirm>
                                                </div>
                                            ))}

                                            {/* Ô thêm ảnh: chọn được nhiều ảnh cùng lúc */}
                                            <button
                                                onClick={() => photoInputRef.current?.click()}
                                                disabled={uploadingPhotos}
                                                className="w-16 h-16 rounded-md border border-dashed border-gray-300 dark:border-[#3a3a3a] flex flex-col items-center justify-center text-gray-400 hover:border-[#3e91ab] hover:text-[#3e91ab] transition disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                                {uploadingPhotos ? <LoadingOutlined /> : <PlusOutlined />}
                                                <span className="text-[10px] mt-0.5">{t('cd.photo_add') || 'Thêm ảnh'}</span>
                                            </button>
                                            <input
                                                ref={photoInputRef}
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                hidden
                                                onChange={handlePhotoInputChange}
                                            />
                                        </div>
                                    </Image.PreviewGroup>
                                </div>

                                <CallReportFail
                                    options={customer?.call_result_options}
                                    value={customer?.last_call_result ?? null}
                                    onReport={(val) => onUpdateField("last_call_result", val)}
                                />

                                <CustomerImgList channelCustomerId={channelCustomerId} />

                                <div
                                    className="
                                        d-none
                                        mt-4
                                        flex items-center justify-between
                                        px-4 py-3
                                        rounded-lg
                                        border border-gray-200
                                        bg-gray-50
                                        cursor-pointer
                                        transition
                                        hover:bg-gray-100
                                        dark:bg-[#1f1f1f]
                                        dark:border-[#303030]
                                        dark:hover:bg-[#262626]
                                    "
                                >
                                    <span className="font-medium text-gray-800 text-000000e0">
                                        {t('cd.dynamic_fields')}
                                    </span>

                                    <span className="text-gray-400 dark:text-gray-500 text-lg">
                                        <RightOutlined className="text-gray-400 dark:text-gray-500 text-[14px]" />
                                    </span>
                                </div>
                            </>
                        ),
                    },
                    {
                        key: "2",
                        label: <span className="d-none">{t('cd.tab_contact')}</span>,
                        children: (
                            <div className="d-none">
                                <ContactList />
                            </div>
                        ),
                    },
                    {
                        key: "3",
                        label: t('cd.tab_channel'),
                        children: (
                            <Channels connections={customer?.connections} />
                        ),
                    },
                ]}
            />
        </Card>
    );
}